<script>
import {
  createProductAccount,
  getProductAccountList,
  updateProductAccount,
  updateProductAccountActive,
} from '@/api/product.js'
import { getBranchList } from '@/api/branch.js'
import { getAdminInfo } from '@/utils/token.js'
import AppPagination from '@/components/common/AppPagination.vue'

const emptyForm = () => ({
  branchCode: '',
  bankName: '',
  accountNo: '',
  accountHolder: '',
  accountType: 'CASH_TRANSFER',
  amountCategory: '기본',
  minAmount: 0,
  maxAmount: null,
  activeYn: 'Y',
})

export default {
  name: 'ProductAccountListPage',
  components: { AppPagination },
  data() {
    return {
      adminInfo: {},
      search: {
        keyword: '',
        branchCode: '',
        accountType: '',
        activeYn: '',
      },
      accountTypeOptions: [
        { title: '전체', value: '' },
        { title: '일반 계좌이체', value: 'CASH_TRANSFER' },
        { title: '위탁제품', value: 'TRUST_PRODUCT' },
        { title: '골드바/실버바', value: 'GOLDBAR_SILVERBAR' },
      ],
      activeOptions: [
        { title: '전체', value: '' },
        { title: '사용', value: 'Y' },
        { title: '미사용', value: 'N' },
      ],
      branchOptions: [{ title: '전체 지점', value: '' }],
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      formDialog: false,
      saving: false,
      form: emptyForm(),
      editTarget: null,
      togglingSet: new Set(),
    }
  },
  computed: {
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
    formTitle() {
      return this.editTarget ? '입금계좌 수정' : '입금계좌 등록'
    },
    selectableBranches() {
      return this.isHq ? this.branchOptions.filter(item => item.value) : this.branchOptions
    },
  },
  mounted() {
    this.adminInfo = getAdminInfo()
    if (!this.isHq && this.adminInfo.branchCode) {
      this.search.branchCode = this.adminInfo.branchCode
      this.form.branchCode = this.adminInfo.branchCode
    }
    this.fetchBranches()
    this.fetchList()
  },
  methods: {
    async fetchBranches() {
      try {
        const res = await getBranchList({ page: 1, size: 500 })
        const rows = res.data?.data?.items || []
        const mapped = rows.map(item => ({
          title: item.branchName || item.branchNm || item.branchCode,
          value: item.branchCode,
        }))
        this.branchOptions = this.isHq ? [{ title: '전체 지점', value: '' }, ...mapped] : mapped
        if (!this.isHq && !this.form.branchCode && mapped[0]) {
          this.form.branchCode = mapped[0].value
          this.search.branchCode = mapped[0].value
        }
      } catch (error) {
        console.error('[ProductAccountListPage] fetchBranches error:', error)
      }
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getProductAccountList({
          keyword: this.search.keyword || undefined,
          branchCode: this.search.branchCode || undefined,
          accountType: this.search.accountType || undefined,
          activeYn: this.search.activeYn || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } catch (error) {
        console.error('[ProductAccountListPage] fetchList error:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = {
        keyword: '',
        branchCode: this.isHq ? '' : this.adminInfo.branchCode,
        accountType: '',
        activeYn: '',
      }
      this.page = 1
      this.fetchList()
    },
    onPageChange(page) {
      this.page = page
      this.fetchList()
    },
    onSizeChange(size) {
      this.size = size
      this.page = 1
      this.fetchList()
    },
    openCreateDialog() {
      this.editTarget = null
      this.form = emptyForm()
      if (!this.isHq) this.form.branchCode = this.adminInfo.branchCode
      this.formDialog = true
    },
    openEditDialog(item) {
      this.editTarget = item
      this.form = {
        branchCode: item.branchCode || '',
        bankName: item.bankName || '',
        accountNo: item.accountNo || '',
        accountHolder: item.accountHolder || '',
        accountType: item.accountType || 'CASH_TRANSFER',
        amountCategory: item.amountCategory || '기본',
        minAmount: Number(item.minAmount || 0),
        maxAmount: item.maxAmount === null || item.maxAmount === undefined ? null : Number(item.maxAmount),
        activeYn: item.activeYn || 'Y',
      }
      this.formDialog = true
    },
    validateForm() {
      if (!this.form.branchCode) return '지점을 선택해 주세요.'
      if (!this.form.bankName) return '은행명을 입력해 주세요.'
      if (!this.form.accountNo) return '계좌번호를 입력해 주세요.'
      if (!this.form.accountHolder) return '예금주를 입력해 주세요.'
      if (!this.form.amountCategory) return '금액구간명을 입력해 주세요.'
      if (Number(this.form.minAmount) < 0) return '최소금액은 0 이상이어야 합니다.'
      if (this.form.maxAmount !== null && this.form.maxAmount !== '' && Number(this.form.maxAmount) < Number(this.form.minAmount)) {
        return '최대금액은 최소금액보다 작을 수 없습니다.'
      }
      return ''
    },
    async saveAccount() {
      const message = this.validateForm()
      if (message) {
        alert(message)
        return
      }
      this.saving = true
      const payload = {
        ...this.form,
        minAmount: Number(this.form.minAmount || 0),
        maxAmount: this.form.maxAmount === '' || this.form.maxAmount === null ? null : Number(this.form.maxAmount),
      }
      try {
        const res = this.editTarget
          ? await updateProductAccount(this.editTarget.branchAccountCode, payload)
          : await createProductAccount(payload)
        if (res.data?.code === 0) {
          this.formDialog = false
          this.fetchList()
        } else {
          alert(res.data?.message || '입금계좌 저장에 실패했습니다.')
        }
      } catch (error) {
        alert(error.response?.data?.message || '입금계좌 저장 중 오류가 발생했습니다.')
      } finally {
        this.saving = false
      }
    },
    async toggleActive(item) {
      if (this.togglingSet.has(item.branchAccountCode)) return
      this.togglingSet = new Set([...this.togglingSet, item.branchAccountCode])
      try {
        const nextValue = item.activeYn === 'Y' ? 'N' : 'Y'
        const res = await updateProductAccountActive(item.branchAccountCode, nextValue)
        if (res.data?.code === 0) {
          item.activeYn = nextValue
        } else {
          alert(res.data?.message || '사용여부 변경에 실패했습니다.')
        }
      } catch (error) {
        alert(error.response?.data?.message || '사용여부 변경 중 오류가 발생했습니다.')
      } finally {
        const updated = new Set(this.togglingSet)
        updated.delete(item.branchAccountCode)
        this.togglingSet = updated
      }
    },
    accountTypeLabel(value) {
      const found = this.accountTypeOptions.find(item => item.value === value)
      return found?.title || value || '-'
    },
    branchTypeLabel(value) {
      const labels = { HQ: '본사', DIRECT: '직영점', AGENCY: '대리점', FRANCHISE: '가맹점' }
      return labels[value] || value || '-'
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '제한 없음'
      return `${Number(value).toLocaleString('ko-KR')}원`
    },
    formatDate(value) {
      return value ? String(value).substring(0, 10) : '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">입금계좌 관리</div>
        <div class="text-body-2 text-grey mt-1">본사와 지점의 계좌이체 입금계좌 및 금액 구간을 관리합니다.</div>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">계좌 등록</v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <v-row class="align-center">
          <v-col cols="12" md="3">
            <v-text-field
              v-model="search.keyword"
              label="검색"
              placeholder="은행, 계좌번호, 예금주, 지점"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              @keyup.enter="handleSearch"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="search.branchCode"
              :items="branchOptions"
              item-title="title"
              item-value="value"
              label="지점"
              variant="outlined"
              density="compact"
              hide-details
              :disabled="!isHq"
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="search.accountType" :items="accountTypeOptions" label="계좌유형" variant="outlined" density="compact" hide-details />
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="search.activeYn" :items="activeOptions" label="사용여부" variant="outlined" density="compact" hide-details />
          </v-col>
          <v-col cols="12" md="2" class="d-flex justify-end ga-2 search-actions">
            <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
            <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          계좌 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th>지점</th>
            <th>계좌유형</th>
            <th>은행</th>
            <th>계좌번호</th>
            <th>예금주</th>
            <th>금액구간</th>
            <th>사용</th>
            <th>등록일</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.branchAccountCode" class="account-row" @click="openEditDialog(item)">
            <td>
              <div class="font-weight-medium">{{ item.branchName || '-' }}</div>
              <div class="text-caption text-grey">{{ branchTypeLabel(item.branchType) }}</div>
            </td>
            <td>{{ accountTypeLabel(item.accountType) }}</td>
            <td>{{ item.bankName }}</td>
            <td class="font-weight-medium">{{ item.accountNo }}</td>
            <td>{{ item.accountHolder }}</td>
            <td>
              <div>{{ item.amountCategory }}</div>
              <div class="text-caption text-grey">{{ formatAmount(item.minAmount) }} ~ {{ formatAmount(item.maxAmount) }}</div>
            </td>
            <td @click.stop>
              <v-switch
                :model-value="item.activeYn === 'Y'"
                color="primary"
                density="compact"
                hide-details
                :loading="togglingSet.has(item.branchAccountCode)"
                @click.stop="toggleActive(item)"
              />
            </td>
            <td class="text-caption">{{ formatDate(item.createdDt) }}</td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="8" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">mdi-bank-off-outline</v-icon>
              등록된 입금계좌가 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>
      <div class="px-4 pb-4">
        <AppPagination :total="total" :page="page" :size="size" @update:page="onPageChange" @update:size="onSizeChange" />
      </div>
    </v-card>

    <v-dialog v-model="formDialog" max-width="720" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-3">{{ formTitle }}</v-card-title>
        <v-divider />
        <v-card-text class="pa-5">
          <v-row>
            <v-col cols="12" md="6">
              <v-select v-model="form.branchCode" :items="selectableBranches" item-title="title" item-value="value" label="지점 *" variant="outlined" :disabled="!isHq" />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="form.accountType" :items="accountTypeOptions.filter(item => item.value)" label="계좌유형 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="form.bankName" label="은행명 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="form.accountNo" label="계좌번호 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="form.accountHolder" label="예금주 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="form.amountCategory" label="금액구간명 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="form.minAmount" type="number" min="0" label="최소금액 *" variant="outlined" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="form.maxAmount" type="number" min="0" label="최대금액" placeholder="비우면 제한 없음" variant="outlined" />
            </v-col>
            <v-col cols="12">
              <v-switch v-model="form.activeYn" true-value="Y" false-value="N" color="primary" label="사용" hide-details />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 justify-end ga-2">
          <v-btn variant="text" @click="formDialog = false">취소</v-btn>
          <v-btn color="primary" variant="flat" :loading="saving" @click="saveAccount">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.account-row {
  cursor: pointer;
}

.account-row:hover {
  background-color: rgba(205, 178, 122, 0.06);
}

@media (max-width: 959px) {
  .search-actions {
    justify-content: flex-start !important;
  }
}
</style>
